import { useState } from "react";
import { Telescope, Workflow, Sparkles, ArrowRight, Zap, ImagePlus, Crop } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import HomeStickyNavbar from "../components/HomeStickyNavbar";


/* ------------------ Service Cards ------------------ */
function OpenSearch({ onOpenFinder }) {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className="group relative bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-blue-200"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Gradient Accent */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-50 to-blue-100 rounded-bl-full opacity-50" />
      
      {/* Icon Container */}
      <div className="relative mb-6 w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center group-hover:bg-blue-100 transition-colors">
        <Telescope size={28} className="text-blue-600" />
      </div>

      {/* Content */}
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="font-semibold text-xl text-gray-900">Tool Finder</h3>
          <Sparkles size={18} className="text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <p className="text-gray-600 text-sm leading-relaxed mb-6">
          Analyzes your complex tasks, breaks them down, and recommends the best AI tools for each part.
        </p>

        {/* Button */}
        <button
          onClick={onOpenFinder}
          className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-all hover:gap-3"
        >
          Get Started
          <ArrowRight size={16} className="transition-transform" />
        </button>
      </div>
    </div>
  );
}

function OpenFlowly({ onOpenFlow }) {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className="group relative bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-red-200"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Gradient Accent */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-50 to-red-100 rounded-bl-full opacity-50" />
      
      {/* Icon Container */}
      <div className="relative mb-6 w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center group-hover:bg-red-100 transition-colors">
        <Workflow size={28} className="text-red-600" />
      </div>

      {/* Content */}
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="font-semibold text-xl text-gray-900">Automation Finder</h3>
          <Zap size={18} className="text-red-600 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <p className="text-gray-600 text-sm leading-relaxed mb-6">
          Flowly, your AI Consultant — delivering ready-to-implement workflows with tool selection, strategy, budget, ROI, and more.
        </p>

        {/* Button */}
        <button
          onClick={onOpenFlow}
          className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white text-sm font-medium rounded-xl hover:bg-red-700 transition-all hover:gap-3"
        >
          Explore Workflows
          <ArrowRight size={16} className="transition-transform" />
        </button>
      </div>
    </div>
  );
}

function OpenDewaan({ onOpenDewaan }) {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className="group relative bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-orange-200"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Floating "New" Badge */}
      <div className="absolute -top-3 -right-3 z-10">
        <div className="relative">
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg animate-bounce">
            NEW
          </div>
          {/* Pulse effect */}
          <div className="absolute inset-0 bg-orange-500 rounded-full animate-ping opacity-75"></div>
        </div>
      </div>
      
      {/* Gradient Accent */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-50 to-orange-100 rounded-bl-full opacity-50" />
      
      {/* Icon Container */}
      <div className="relative mb-6 w-14 h-14 bg-orange-50 rounded-xl flex items-center justify-center group-hover:bg-orange-100 transition-colors">
        <ImagePlus size={28} className="text-orange-600" />
      </div>
      {/* Content */}
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="font-semibold text-xl text-gray-900">Dewaan</h3>
          <Crop size={18} className="text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <p className="text-gray-600 text-sm leading-relaxed mb-6">
          The AI design platform built for flawless Arabic creativity.
        </p>
        {/* Button */}
        <button
          onClick={onOpenDewaan}
          className="inline-flex items-center gap-2 px-6 py-3 bg-orange-900 text-white text-sm font-medium rounded-xl hover:bg-orange-700 transition-all hover:gap-3"
        >
          Try Arabic Magic
          <ArrowRight size={16} className="transition-transform" />
        </button>
      </div>
    </div>
  );
}

/* ------------------ Welcome Hero ------------------ */
function WelcomeHero({ Name }) {
  return (
    <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-10 sm:p-12 overflow-hidden shadow-2xl">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 right-10 w-64 h-64 bg-blue-500 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-64 h-64 bg-red-500 rounded-full blur-3xl" />
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }} />

      {/* Content */}
      <div className="relative z-10">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6 border border-white/20">
          <span className="text-2xl">👋</span>
          <span className="text-white/90 text-sm font-medium">Welcome back</span>
        </div>

        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
          Hey {Name || "there"}!
        </h1>
        
        <p className="text-gray-300 text-lg sm:text-xl max-w-2xl leading-relaxed mb-8">
          Ready to supercharge your workflow? Choose your path below and let AI transform how you work.
        </p>

        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
            <span className="text-white/80 text-sm">AI-Powered Discovery</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-white/80 text-sm">Workflow Automation</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------ Stats Bar ------------------ */
function StatsBar() {
  const stats = [
    { label: "AI Tools Indexed", value: "300+" },
    { label: "Workflows Generated", value: "1K+" },
    { label: "Hours Saved", value: "1.5K+" }
  ];

  return (
    <div className="grid grid-cols-3 gap-6 w-full">
      {stats.map((stat, idx) => (
        <div key={idx} className="text-center">
          <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
            {stat.value}
          </div>
          <div className="text-xs sm:text-sm text-gray-500">
            {stat.label}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------ Home Page ------------------ */
const Home = () => {
  const name = Cookies.get("fname");
  const navigate = useNavigate();

  function navigateToFinder() {
    navigate("/finder");
  }

  function navigateToFlow() {
    navigate("/tuly");
  }

  function navigateToDewaan() {
    navigate("/golden");
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <HomeStickyNavbar />

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 pt-28 pb-16">
        <div className="space-y-12">
          
          {/* Welcome Hero */}
          <WelcomeHero Name={name} />

          {/* Stats Bar */}
          <StatsBar />

          {/* Service Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            <OpenDewaan onOpenDewaan={navigateToDewaan} />
            <OpenSearch onOpenFinder={navigateToFinder} />
            <OpenFlowly onOpenFlow={navigateToFlow} />
            
          </div>

          {/* Bottom CTA 
          <div className="text-center pt-8">
            <p className="text-gray-500 text-sm">
              Need help getting started?{" "}
              <button className="text-blue-600 hover:text-blue-700 font-medium underline">
                View our guide
              </button>
            </p>
          </div>*/}

        </div>
      </div>
    </div>
  );
};

export default Home;